import { useChains } from "@opensea/app-kit";
import { NumberDisplay, SwitchPill, Tooltip } from "@opensea/ui-kit";
import { BigNumber, bn } from "@opensea/utils";
import { useEffect, useMemo, useState } from "react";
import { useClient as useUrqlClient } from "urql";
import { useTranslations } from "use-intl";
import { useBaseGasPrice } from "../../hooks/useBaseGasPrice";
const SVM_BPS_FEE = 0.0085;
export function CurrencyQuantityPresetButton({
  balance,
  balanceMultiplier = 1,
  quantity,
  onClick,
  requiresSvmFee = true,
  getFeeBufferAmount: getFeeBufferAmountProp,
  publicClient,
  gasLimit
}) {
  const t = useTranslations("wallet.CurrencyQuantityPresetButton");
  const {
    getNativeCurrency
  } = useChains();
  const urqlClient = useUrqlClient();
  const [availableAmount, setAvailableAmount] = useState(0);
  const nativeCurrency = balance ? getNativeCurrency(balance.currency.chainIdentifier) : undefined;
  const isNativeCurrency = nativeCurrency?.contractAddress === balance?.currency.contractAddress;
  const {
    gas
  } = useBaseGasPrice({
    chain: balance?.currency.chainIdentifier,
    tokenAddress: balance?.currency.contractAddress
  });
  const balanceQuantity = balance ? bn(balance.quantity).times(balanceMultiplier).dp(Math.max(balance.currency.decimals - 1, 0), BigNumber.ROUND_FLOOR).toNumber() : 0;
  const calculateAvailableAmount = async () => {
    let amount = bn(balanceQuantity);
    if (balanceMultiplier === 1 && isNativeCurrency && balance) {
      let feeBuffer = bn(0);
      if (getFeeBufferAmountProp && (balance.currency.chain.arch === "EVM" || balance.currency.chain.arch === "SVM") && balance.quantity) {
        try {
          const balanceBn = bn(balance.quantity);
          const bufferBn = await getFeeBufferAmountProp(balance.currency.chain.arch, balance.currency.chain.identifier, balanceBn, publicClient ?? null, gasLimit, urqlClient ?? null);
          feeBuffer = bufferBn;
        } catch (error) {
          console.error("Failed to get dynamic buffer, using static fallback:", error);
        }
      }
      if (feeBuffer.isZero() && gas > 0) {
        feeBuffer = bn(gas);
      }
      if (feeBuffer.gt(0)) {
        amount = amount.minus(feeBuffer);
      }
    } else if (isNativeCurrency) {
      amount = amount.minus(gas);
    }
    if (balance?.currency.chain.arch === "SVM" && isNativeCurrency && requiresSvmFee) {
      amount = amount.times(bn(1).minus(SVM_BPS_FEE));
    }
    return amount.toNumber();
  };
  useEffect(() => {
    calculateAvailableAmount().then(setAvailableAmount);
  }, [balance, balanceMultiplier, gas, isNativeCurrency, getFeeBufferAmountProp, publicClient, gasLimit, urqlClient]);
  const handleMouseEnter = () => {
    if (balanceMultiplier === 1 && isNativeCurrency && getFeeBufferAmountProp && balance?.quantity) {
      const balanceBn_0 = bn(balance.quantity);
      if (balance.currency.chain.arch === "EVM" && publicClient) {
        getFeeBufferAmountProp(balance.currency.chain.arch, balance.currency.chain.identifier, balanceBn_0, publicClient, gasLimit, urqlClient ?? null).catch(error_0 => {
          console.debug("Gas buffer pre-fetch failed:", error_0);
        });
      } else if (balance.currency.chain.arch === "SVM") {
        getFeeBufferAmountProp(balance.currency.chain.arch, balance.currency.chain.identifier, balanceBn_0, null, gasLimit, urqlClient ?? null).catch(error_1 => {
          console.debug("Gas buffer pre-fetch failed:", error_1);
        });
      }
    }
  };
  const checked = quantity === availableAmount;
  const disabledReason = useMemo(() => {
    if (!balance) {
      return t("noToken");
    }
    if (availableAmount < 0) {
      return t("insufficientBalance");
    }
    return undefined;
  }, [balance, quantity, availableAmount, t]);
  const disabled = Boolean(disabledReason);
  return <Tooltip content={disabledReason} disabled={!disabled}>
      <span>
        <SwitchPill checked={checked} disabled={disabled} onClick={() => {
        onClick(availableAmount);
      }} onMouseEnter={handleMouseEnter} variant="transparent">
          {balanceMultiplier === 1 ? t("max") : <NumberDisplay display="percent" value={balanceMultiplier} />}
        </SwitchPill>
      </span>
    </Tooltip>;
}